// Phase 16 — Navigation Assistant (floating helper bubble + role-aware tour)
//
// Visibility rules
// ----------------
// • Hidden for admin and master_admin roles (server also blocks)
// • Hidden after the user disables it (persisted via /api/navigation-help/me/disable)
// • Hidden after `seen_count >= max_impressions` (default 10)
// • Visible by default for customer / affiliate / driver / partner / franchisee
//
// Each render counts as an impression (POST /me/seen). The user can:
//   • Start a quick tour (in-page highlight overlay)
//   • Skip for now (closes this session only)
//   • Turn off navigation help (persistent)

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, ArrowRight, ArrowLeft, Power } from "lucide-react";
import api from "@/lib/api";

// --- Role → tour script lookup --------------------------------------------
// Each step targets the most useful surfaces a fresh user lands on first.
// Selector strings prefer data-testid attributes (stable across UI refreshes).
const TOUR_STEPS = {
  customer: [
    { selector: "[data-testid=shell-sidebar]",       title: "Your dashboard sidebar",
      body: "All your tools live here — shop, training, messages, network." },
    { selector: "[data-testid=shell-logout-btn]",    title: "Sign out anytime",
      body: "Sign out from any device using this button at the bottom of the sidebar." },
  ],
  affiliate: [
    { selector: "[data-testid=aff-billing-badge]",   title: "Your access badge",
      body: "Tier, payment status, and Partner Affiliate state live right here." },
    { selector: "a[href='/affiliate/tracking']",     title: "Affiliate Links",
      body: "Generate referral links + QR codes. The Picker tab finds products visually." },
    { selector: "a[href='/affiliate/commissions']",  title: "Commissions",
      body: "Track every sale your referrals drive — by product, period, and tier." },
    { selector: "a[href='/training']",               title: "Training & certification",
      body: "Unlock higher tier features by completing the Lifted Affiliate Academy." },
    { selector: "a[href='/messages']",               title: "Direct messages",
      body: "DM any active driver, partner, franchisee, or fellow affiliate." },
    { selector: "a[href='/network']",                title: "The Launchpad",
      body: "The shared Lifted Network feed — your post reaches the whole ecosystem." },
  ],
  driver: [
    { selector: "a[href='/delivery']",               title: "Dispatch dashboard",
      body: "Today's queue + active delivery, real-time. Tap a row to navigate." },
    { selector: "a[href='/delivery']",               title: "Deliveries",
      body: "Accept, pickup, and confirm drops. Each step writes to the ledger automatically." },
    { selector: "a[href='/delivery']",               title: "Routes",
      body: "TomTom routing optimizes the order of your stops — follow the highlighted leg." },
    { selector: "a[href='/training']",               title: "Driver Training",
      body: "Complete the Driver track + safety modules before you go on shift." },
    { selector: "a[href='/messages']",               title: "Dispatch & support",
      body: "DM dispatch or a partner directly when something changes mid-route." },
  ],
  partner: [
    { selector: "a[href='/partner']",                title: "Partner dashboard",
      body: "Today's orders, fulfillment SLA, and your storefront snapshot." },
    { selector: "a[href='/partner/inventory']",      title: "Inventory tools",
      body: "Manage product stock, prices, compliance status, and same-day availability." },
    { selector: "a[href='/partner/orders']",         title: "Orders",
      body: "Incoming Lifted orders — accept, prep, hand off to a driver." },
    { selector: "a[href='/training']",               title: "Partner Training",
      body: "Compliance + fulfillment + Lifted brand training modules." },
    { selector: "a[href='/messages']",               title: "Talk to drivers + admins",
      body: "Reach drivers, franchise managers, and Lifted admins instantly." },
  ],
  franchisee: [
    { selector: "a[href='/franchise']",              title: "Franchise dashboard",
      body: "Territory snapshot — partners, drivers, orders, compliance, revenue." },
    { selector: "a[href='/franchise/territory']",    title: "Territory",
      body: "Your ZIP grid, restricted product map, and same-day delivery coverage." },
    { selector: "a[href='/franchise/partners']",     title: "Operations",
      body: "Onboard storefront partners + drivers, monitor fulfillment, escalate compliance." },
    { selector: "a[href='/franchise/orders']",       title: "Inventory + orders",
      body: "Cross-partner inventory health and the live order pipeline for your territory." },
    { selector: "a[href='/network']",                title: "The Launchpad",
      body: "Announcements from HQ + cross-franchise discussions." },
  ],
  lifted_partner: [
    { selector: "a[href='/lifted-partner']",         title: "Mentor console",
      body: "Active affiliates, training % completion, and pending review queues at a glance." },
    { selector: "a[href='/lifted-partner/affiliates']", title: "Affiliates you mentor",
      body: "Every affiliate in your circle — referral count, last conversion, tier." },
    { selector: "a[href='/lifted-partner/pending']", title: "Pending reviews",
      body: "Help admins triage pending partner and driver applications." },
    { selector: "a[href='/messages']",               title: "DM new members",
      body: "Welcome new affiliates / partners / drivers directly — they see your role." },
  ],
};

function primaryRole(roles) {
  if (!roles || roles.length === 0) return "customer";
  // Priority order — mentor / operational roles win over plain customer
  for (const r of ["lifted_partner", "franchisee", "partner", "driver", "affiliate", "customer"]) {
    if (roles.includes(r)) return r;
  }
  return roles[0];
}

export default function NavigationAssistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe]               = useState(null);
  const [helpState, setHelpState] = useState(null);
  const [open, setOpen]           = useState(false);
  const [tour, setTour]           = useState({ active: false, step: 0 });
  const [highlightRect, setHighlightRect] = useState(null);
  const [skippedThisSession, setSkippedThisSession] = useState(false);

  // -- Load /auth/me + /navigation-help/me on mount -----------------------
  useEffect(() => {
    let alive = true;
    api.get("/auth/me")
      .then((r) => { if (alive) setMe(r.data); })
      .catch(() => { if (alive) setMe({ roles: [] }); });
    api.get("/navigation-help/me")
      .then((r) => { if (alive) setHelpState(r.data); })
      .catch(() => { if (alive) setHelpState({ should_show: false }); });
    return () => { alive = false; };
  }, []);

  const role = useMemo(() => primaryRole(me?.roles), [me]);
  const isAdmin = (me?.roles || []).some((r) => r === "admin" || r === "master_admin");
  const shouldShow = helpState?.should_show && !isAdmin && !skippedThisSession;

  // -- Bump the impression counter once per LOGIN (not per route) ----------
  // Phase 18 — spec is "first 3 logins". We use sessionStorage so each fresh
  // browser session counts exactly one impression even if the user navigates
  // across many routes. Cleared automatically when the tab closes.
  useEffect(() => {
    if (!shouldShow) return;
    const KEY = "lifted_navhelp_session_bumped";
    if (sessionStorage.getItem(KEY) === "1") return;
    sessionStorage.setItem(KEY, "1");
    api.post("/navigation-help/me/seen").then((r) => setHelpState(r.data)).catch(() => {});
  }, [shouldShow]);

  // -- Tour helpers --------------------------------------------------------
  const steps = TOUR_STEPS[role] || TOUR_STEPS.customer;
  const currentStep = steps[tour.step];

  const positionHighlight = useCallback(() => {
    if (!tour.active || !currentStep) {
      setHighlightRect(null);
      return;
    }
    const el = document.querySelector(currentStep.selector);
    if (!el) { setHighlightRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    setHighlightRect({
      top:  r.top - 6, left: r.left - 6,
      width: r.width + 12, height: r.height + 12,
    });
  }, [tour, currentStep]);

  useEffect(() => {
    positionHighlight();
    window.addEventListener("resize", positionHighlight);
    window.addEventListener("scroll", positionHighlight, true);
    return () => {
      window.removeEventListener("resize", positionHighlight);
      window.removeEventListener("scroll", positionHighlight, true);
    };
  }, [positionHighlight]);

  function startTour() {
    setOpen(false);
    setTour({ active: true, step: 0 });
  }
  function endTour(completed = false) {
    setTour({ active: false, step: 0 });
    setHighlightRect(null);
    if (completed) {
      api.patch("/navigation-help/me", { tour_completed: true })
         .then((r) => setHelpState(r.data)).catch(() => {});
    }
  }
  function next() {
    if (tour.step + 1 < steps.length) {
      setTour({ active: true, step: tour.step + 1 });
    } else {
      endTour(true);
    }
  }
  function back() { setTour({ active: true, step: Math.max(0, tour.step - 1) }); }
  async function disableHelper() {
    await api.post("/navigation-help/me/disable").catch(() => {});
    setHelpState({ ...(helpState || {}), should_show: false, enabled: false });
    setOpen(false);
    endTour();
  }

  // ----------------------------------------------------------------------
  // Render gates
  if (!shouldShow && !tour.active) return null;

  return (
    <>
      {/* Tour overlay */}
      {tour.active && (
        <div
          className="fixed inset-0 z-[9999] pointer-events-none"
          data-testid="nav-tour-overlay"
        >
          {/* Dim everything except the highlight */}
          <div className="absolute inset-0 bg-black/60 pointer-events-auto" onClick={() => endTour(false)} />
          {highlightRect && (
            <div
              className="absolute pointer-events-none border-2 border-lifted-green rounded-xl"
              style={{
                top: highlightRect.top,  left: highlightRect.left,
                width: highlightRect.width, height: highlightRect.height,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.65)",
                transition: "all 200ms ease-out",
              }}
            />
          )}
          {/* Step card */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 max-w-md w-[92%] sm:w-[420px] pointer-events-auto">
            <div className="glass-panel p-4 border border-lifted-green/40 backdrop-blur-xl"
                 data-testid="nav-tour-card">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green uppercase">
                  Step {tour.step + 1} of {steps.length}
                </span>
                <button onClick={() => endTour(false)} className="text-lifted-dim hover:text-white"
                        data-testid="nav-tour-close" aria-label="Close tour">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <h3 className="font-anton text-white text-xl uppercase leading-tight mb-1">
                {currentStep?.title || ""}
              </h3>
              <p className="text-lifted-dim text-sm mb-3">{currentStep?.body || ""}</p>
              <div className="flex items-center justify-between gap-2">
                <button onClick={back} disabled={tour.step === 0}
                        className="btn-lifted-ghost inline-flex items-center gap-1 text-xs disabled:opacity-40"
                        data-testid="nav-tour-back">
                  <ArrowLeft className="h-3 w-3" /> Back
                </button>
                <button onClick={disableHelper}
                        className="text-[0.65rem] text-lifted-dim hover:text-rose-300 inline-flex items-center gap-1"
                        data-testid="nav-tour-disable">
                  <Power className="h-3 w-3" /> Turn off help
                </button>
                <button onClick={next}
                        className="btn-lifted inline-flex items-center gap-1 text-xs"
                        data-testid="nav-tour-next">
                  {tour.step + 1 < steps.length ? <>Next <ArrowRight className="h-3 w-3" /></> : "Finish"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating bubble (hidden while tour is active) */}
      {!tour.active && shouldShow && (
        <div className="fixed bottom-5 right-5 z-[8888]" data-testid="nav-help-bubble">
          {!open ? (
            <button
              onClick={() => setOpen(true)}
              className="glass-panel border border-lifted-green/40 rounded-full pl-3 pr-4 py-2 inline-flex items-center gap-2 hover:border-lifted-green hover:shadow-lg hover:shadow-lifted-green/20 transition"
              data-testid="nav-help-bubble-open"
              aria-label="Open navigation help"
            >
              <span className="rounded-full bg-lifted-green/20 p-1">
                <Sparkles className="h-3.5 w-3.5 text-lifted-green" />
              </span>
              <span className="text-xs text-white font-bebas tracking-[0.18em] uppercase">
                Need help?
              </span>
            </button>
          ) : (
            <div className="glass-panel p-4 max-w-sm w-[88vw] sm:w-[360px] border border-lifted-green/40 backdrop-blur-xl"
                 data-testid="nav-help-bubble-panel">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-green uppercase mb-1">
                    Navigation help
                  </div>
                  <h3 className="font-anton text-white text-lg uppercase leading-tight">
                    Need help navigating the Lifted ecosystem?
                  </h3>
                </div>
                <button onClick={() => { setOpen(false); setSkippedThisSession(true); }}
                        className="text-lifted-dim hover:text-white"
                        data-testid="nav-help-bubble-close"
                        aria-label="Skip for now">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-lifted-dim text-xs mt-2">
                We'll walk you through your dashboard, training, links, and messaging — about 30 seconds.
              </p>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <button onClick={startTour}
                        className="btn-lifted inline-flex items-center gap-1 text-xs"
                        data-testid="nav-help-start-tour">
                  Start quick tour <ArrowRight className="h-3 w-3" />
                </button>
                <button onClick={() => { setOpen(false); setSkippedThisSession(true); }}
                        className="btn-lifted-ghost text-xs"
                        data-testid="nav-help-skip">
                  Skip for now
                </button>
              </div>
              <div className="mt-3 border-t border-white/10 pt-3">
                <button onClick={disableHelper}
                        className="text-[0.7rem] text-lifted-dim hover:text-rose-300 inline-flex items-center gap-1"
                        data-testid="nav-help-disable">
                  <Power className="h-3 w-3" /> Turn off website navigation assistance
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
